import type {
  AxiosInstance,
  AxiosResponse,
  RawAxiosRequestConfig,
} from "axios";
import { createRequestFunction } from "./common";

/**
 *
 * @export
 * @interface RequestArgs
 */
export interface RequestArgs {
  url: string;
  options: RawAxiosRequestConfig;
  hasFile?: boolean;
  displayError?: boolean;
}

/**
 *
 * @export
 * @enum {string}
 */
export enum RequestMethod {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  DELETE = "DELETE",
  PATCH = "PATCH",
}

/**
 *
 * @export
 * @class BaseAPI
 */
export class BaseAPI<D = {}, R = {}> {
  protected route = "";
  protected method: RequestMethod = RequestMethod.GET;
  protected params: string[] = [];
  protected hasFile = false;
  protected displayError = true;

  constructor(protected axios: AxiosInstance) { }

  public async call(
    data: D,
    options?: RawAxiosRequestConfig,
  ): Promise<AxiosResponse<R, any>> {
    let route = this.route;
    const useMockApi = this.axios.defaults.useMockApi;

    // Collect query params if needed
    const queryParams: Record<string, string> = {};
    this.params.forEach((param) => {
      const paramValue = data[param as keyof D];
      const paramData =
        paramValue !== undefined && paramValue !== null
          ? encodeURIComponent(paramValue as string)
          : "";
      if (route.includes(`{${param}}`)) {
        route = route.replace(`{${param}}`, paramData);
      } else if (paramValue !== undefined && paramValue !== null) {
        queryParams[param] = paramValue as string;
      }
      // Remove from body
      delete data[param as keyof D];
    });

    // Convert data to FormData if hasFile is true and method is POST/PUT/PATCH
    let requestData = data;
    if (this.hasFile && (this.method === RequestMethod.POST || this.method === RequestMethod.PUT || this.method === RequestMethod.PATCH) && !useMockApi) {
      const formData = new FormData();
      Object.entries(data as Record<string, any>).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (key === 'feature_ids' && Array.isArray(value)) {
            if (value.length === 0) {
              formData.append('feature_ids', ''); // Send empty string to indicate intentional empty list
            } else {
              value.forEach((v: any) => formData.append('feature_ids', v));
            }
          } else if (key === 'deleted_image_indices' && Array.isArray(value)) {

            value.forEach((v: any) => {
              formData.append('deleted_image_indices', v);
            });
          } else if (value instanceof File) {
            formData.append(key, value);
          } else if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
            // For property_images: File[]
            value.forEach((file: File) => formData.append(key, file));
          } else {
            formData.append(key, String(value));
          }
        }
      });
      requestData = formData as any;
    }

    const axiosArgs: RequestArgs = {
      url: useMockApi
        ? "/mock" +
        (route.includes("?")
          ? route.split("?")[0] + ".json?" + route.split("?")[1]
          : route + ".json")
        : route,
      hasFile: this.hasFile,
      displayError: this.displayError,
      options: {
        ...options,
        method: useMockApi ? RequestMethod.GET : this.method,
        // For GET requests, use queryParams as params
        ...(this.method === RequestMethod.GET && !useMockApi
          ? { params: queryParams }
          : { data: requestData }),
        // For POST/PUT/PATCH, add query params if present
        ...((this.method === RequestMethod.POST || this.method === RequestMethod.PUT || this.method === RequestMethod.PATCH) && Object.keys(queryParams).length > 0
          ? { params: queryParams }
          : {}),
      },
    };

    const request = createRequestFunction(axiosArgs);

    if (useMockApi) {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    return request<R>(this.axios);
  }
}